









































// vue
import {
    defineComponent,
    onMounted,
    onUnmounted,
    ref,
} from '@vue/composition-api';

// hooks
import { useGeoRecord } from '@/vue3/hooks/geoRecordHook';
import { createRectangleGeoRecord } from '@/vue3/hooks/createRectangleHook';
import { useGeospatial } from '@/vue3/hooks/geospatialHook';
import { updateGeoRecordRotation } from '@/vue3/hooks/geoRecordRotationHook';

// primevue
import InputText from 'primevue/inputtext';
import SelectButton from 'primevue/selectbutton';

// services
import { useService } from '@/vue3/api/services';

// store
import { useStore } from '@/store';

// models
import { GeoRecord } from '@/vue3/api/models';

// turf
import * as turf from '@turf/turf';

export default defineComponent({
    components: {
        InputText,
        SelectButton,
    },
    props: {
        mapbox: {
            type: Object,
            default: () => {},
        },
        saveGeoRecord: {
            type: Function,
            default: () => {},
        },
        activateCursor: {
            type: Function,
            default: () => {},
        },
        createNewGeoRecord: {
            type: Function,
            default: () => {},
        },
        geoRecord: {
            type: Object,
            default: () => {},
        },
        isRectangleEditor: {
            type: Boolean,
            default: false,
        },
        newCategoryDefaultId: {
            type: Number,
            default: 0,
        },
    },
    setup(props: any) {
        // store
        const store = useStore();

        // model
        const newGeoRecord = ref({} as GeoRecord);

        // hooks
        const {
            initRectangleGeoRecordSource,
            removeRectangleGeoRecordSource,
            removeCursorSource,
            updateVertexLabel,
        } = useGeoRecord();
        const { translate } = useGeospatial();
        const { getSrid } = useService();
        const { createRectangle } = createRectangleGeoRecord();
        const { updateRotationDegree } = updateGeoRecordRotation();

        // reactive
        const width = ref();
        const height = ref();
        const calculatedWidth = ref();
        const calculatedHeight = ref();
        const selectButtonOption = ref();
        const courseSrid = ref(0);
        const isRectangleLayerVisible = ref(false);
        const isRectangleToolSelected = ref(false);
        const editing = ref(false);
        const isLoading = ref<boolean>(false);
        const unitOptions = ref([
            { name: 'Metre', value: 'm' },
            { name: 'Feet', value: 'ft' },
            { name: 'Yard', value: 'y' },
        ]);
        const isMeters = store.state.isMeters;

        // methods
        const getSridOnCourseSchemaId = async () => {
            courseSrid.value = await getSrid(
                store.getters.currentCourseSchema.id,
            );
        };

        const getRectangle = () => {
            width.value = null;
            height.value = null;
            newGeoRecord.value = {} as GeoRecord;
            if (props.mapbox.map.getLayer('rectangle-geo-record-polygon')) {
                removeRectangleGeoRecordSource(props.mapbox.map);
            }
            props.mapbox.map.off('draw.create', props.createNewGeoRecord);
            props.mapbox.map.on('click', onMouseClick);
            props.mapbox.map.on('touchend', mobileCreateRectangle);
        };

        const mobileCreateRectangle = (e: any) => {
            if (!isRectangleLayerVisible.value) {
                isRectangleLayerVisible.value = true;
            }
            getRectangleGeometry(e.lngLat);
            onMouseClick();
        };

        const updateRectangleGeometry = () => {
            if (props.geoRecord) {
                if (width.value > 0 && height.value > 0) {
                    getRectangleGeometry({
                        lng: props.geoRecord.geometry.coordinates[0][0][0],
                        lat: props.geoRecord.geometry.coordinates[0][0][1],
                    });
                }
            } else {
                if (props.mapbox.map.getLayer('rectangle-geo-record-polygon')) {
                    removeRectangleGeoRecordSource(props.mapbox.map);
                    isRectangleLayerVisible.value = false;

                    props.activateCursor();
                }
                if (width.value > 0 && height.value > 0) {
                    props.mapbox.map.on('mousemove', moveGeoRecordLayer);
                }
            }
        };

        const onMouseClick = () => {
            if (isRectangleLayerVisible.value) {
                editing.value = true;
                isLoading.value = true;
                isRectangleToolSelected.value = false;
                newGeoRecord.value.accountId = store.getters.account.id;
                newGeoRecord.value.courseSchemaId =
                    store.getters.currentCourseSchema.id;
                newGeoRecord.value.organizationId =
                    store.getters.currentOrganization.id;
                newGeoRecord.value.isFill = true;
                newGeoRecord.value.isRectangle = true;
                newGeoRecord.value.rotation = props.geoRecord?.rotation;
                isRectangleToolSelected.value = false;
                newGeoRecord.value.geoRecordCategoryId =
                    props.newCategoryDefaultId;
                if (newGeoRecord.value.geometry) {
                    props.saveGeoRecord(newGeoRecord.value);
                }
                getRectangle();
            }
        };

        const moveGeoRecordLayer = ({ lngLat }) => {
            if (width.value !== undefined && height.value !== undefined) {
                if (width.value > 0 && height.value > 0) {
                    removeCursorSource(props.mapbox.map);
                    getRectangleGeometry(lngLat);

                    if (!isRectangleLayerVisible.value) {
                        isRectangleLayerVisible.value = true;
                        initRectangleGeoRecordSource(
                            props.mapbox.map,
                            newGeoRecord.value,
                        );
                    }

                    const source = props.mapbox.map.getSource(
                        'rectangle-geo-records',
                    );

                    if (source) {
                        let feature = source._data;
                        const centroid =
                            turf.centroid(feature).geometry?.coordinates;
                        if (!centroid) {
                            return;
                        }
                        feature = translate(feature, centroid, [
                            lngLat.lng,
                            lngLat.lat,
                        ]);
                        newGeoRecord.value.geometry = feature.geometry;
                        source.setData(feature);
                    }
                }
            }
        };

        const checkParameters = () => {
            if (selectButtonOption.value.value === 'y') {
                calculatedWidth.value = parseFloat(width.value) * 0.9144;
                calculatedHeight.value = parseFloat(height.value) * 0.9144;
            } else if (selectButtonOption.value.value === 'ft') {
                calculatedWidth.value = parseFloat(width.value) * 0.3048;
                calculatedHeight.value = parseFloat(height.value) * 0.3048;
            } else if (selectButtonOption.value.value === 'm') {
                calculatedWidth.value = parseFloat(width.value);
                calculatedHeight.value = parseFloat(height.value);
            }
        };

        const changeRectangleGeometry = (lngLat) => {
            checkParameters();
            newGeoRecord.value.geometry = createRectangle(
                Object.values(lngLat),
                calculatedWidth.value,
                calculatedHeight.value,
                courseSrid.value,
            );
            props.mapbox.draw.add({
                id: 'test',
                type: 'Feature',
                properties: { rotation: props.geoRecord.rotation },
                geometry: newGeoRecord.value.geometry,
                height: newGeoRecord.value.height,
            });
            let feature = props.mapbox.draw.get('test');
            const centroid = turf.centroid(feature).geometry?.coordinates;
            if (!centroid) {
                return;
            }
            const newCentroid = ref<any>([]);
            newCentroid.value = turf.centroid(
                props.geoRecord.geometry,
            ).geometry?.coordinates;
            feature = translate(feature, centroid, newCentroid.value);

            if (props.mapbox.map.getLayer('rectangle-geo-record-polygon')) {
                removeRectangleGeoRecordSource(props.mapbox.map);
            }

            newGeoRecord.value.geometry = feature.geometry;
            props.mapbox.draw.deleteAll();
            props.mapbox.draw.add({
                id: 'test',
                type: 'Feature',
                properties: {
                    rotation: props.geoRecord.rotation,
                    isRectangle: props.geoRecord.isRectangle,
                    vertexLabelState: props.geoRecord.vertexLabelState,
                    smoothCount: 0,
                },
                geometry: feature.geometry,
            });
            updateRotationDegree(
                props.mapbox.draw,
                props.geoRecord.rotation + props.geoRecord.rotation,
            );
            updateVertexLabel(
                props.mapbox.draw.get('test'),
                props.mapbox.map,
                isMeters,
            );
        };

        const getRectangleGeometry = (lngLat) => {
            if (props.geoRecord) {
                changeRectangleGeometry(lngLat);
            } else {
                checkParameters();
                newGeoRecord.value.geometry = createRectangle(
                    lngLat.toArray(),
                    calculatedWidth.value,
                    calculatedHeight.value,
                    courseSrid.value,
                );
            }
        };

        const getMeasurement = () => {
            var fromX = turf.point(props.geoRecord.geometry.coordinates[0][0]);
            var toX = turf.point(props.geoRecord.geometry.coordinates[0][1]);
            var fromY = turf.point(props.geoRecord.geometry.coordinates[0][3]);
            var toY = turf.point(props.geoRecord.geometry.coordinates[0][4]);
            var distanceX =
                turf.distance(fromX, toX, {
                    units: 'miles',
                }) / 0.00062137;
            var distanceY =
                turf.distance(fromY, toY, {
                    units: 'miles',
                }) / 0.00062137;

            height.value = distanceY.toFixed();
            width.value = distanceX.toFixed();
        };

        const updateRectangleDimensions = () => {
            getMeasurement();
            props.mapbox.draw.deleteAll();
            props.mapbox.draw.changeMode('simple_select');
            if (props.geoRecord !== undefined) {
                props.mapbox.draw.add({
                    id: 'test',
                    type: 'Feature',
                    geometry: props.geoRecord.geometry,
                });
            }
        };

        // lifecycle
        onMounted(() => {
            getSridOnCourseSchemaId();
            selectButtonOption.value = { name: 'Metre', value: 'm' };
            if (props.geoRecord !== undefined) {
                updateRectangleDimensions();
            } else {
                getRectangle();
            }
        });

        onUnmounted(() => {
            props.mapbox.map.off('mousemove', moveGeoRecordLayer);
            props.mapbox.map.off('click', onMouseClick);
            if (props.mapbox.map.getLayer('rectangle-geo-record-polygon')) {
                removeRectangleGeoRecordSource(props.mapbox.map);
            }
        });

        onUnmounted(() => {
            props.mapbox.map.off('touchend', mobileCreateRectangle);
        });

        return {
            unitOptions,
            updateRectangleGeometry,
            selectButtonOption,
            width,
            height,
        };
    },
});
